import { LinkButton, SelectInput, SelectInputItem, TextField } from '@ads/components-react';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker, Skeleton } from '@/shared/ui/atoms';

import { useBannerPriorityOptions } from '../../../hooks/useBannerPriorityOptions';
import type { BannerFormValues } from '../../../types/bannerForm';
import { BANNER_ERROR_ANCHORS } from '../../../utils/bannerFormErrors';
import { formatDurationLabel } from '../../../utils/bannerMedia';
import { BannerFieldLabel } from '../../molecules/BannerFieldLabel';
import { BannerFormSection } from '../../molecules/BannerFormSection';
import { CompanyAutocomplete } from '../../molecules/CompanyAutocomplete';

export interface BannerInfoSectionProps {
  readonly control: Control<BannerFormValues>;
  readonly errors: FieldErrors<BannerFormValues>;
  /** Rótulo da empresa já selecionada (vem de `useCompanyById` na edição). */
  readonly companyLabel: string | undefined;
  readonly onCompanyLabelChange: (label: string | undefined) => void;
  /** Duração travada pelo vídeo; quando informada, o campo fica somente leitura. */
  readonly videoDurationSeconds: number | null;
}

/**
 * Bloco "Informações" do formulário de Banner: Empresa, Título, período,
 * Prioridade, Url e Duração.
 *
 * A Duração é editável com imagem e **somente leitura** quando há vídeo
 * escolhido — nesse caso o valor vem da duração do arquivo e o texto de apoio
 * diz de onde veio.
 */
export function BannerInfoSection({
  control,
  errors,
  companyLabel,
  onCompanyLabelChange,
  videoDurationSeconds,
}: BannerInfoSectionProps) {
  const { t } = useTranslation('banner');
  const {
    options: priorityOptions,
    isLoading: isPriorityLoading,
    isError: isPriorityError,
    refetch: refetchPriorities,
  } = useBannerPriorityOptions();

  const isDurationLocked = videoDurationSeconds !== null && videoDurationSeconds > 0;

  return (
    <BannerFormSection title={t('banner.form.info.heading')}>
      <div className="grid grid-cols-1 gap-mini md:grid-cols-2">
        <Controller
          control={control}
          name="companyId"
          render={({ field }) => (
            <CompanyAutocomplete
              value={field.value ?? null}
              {...(companyLabel !== undefined ? { selectedLabel: companyLabel } : {})}
              onChange={(id, label) => {
                field.onChange(id ?? undefined);
                onCompanyLabelChange(label || undefined);
              }}
              label={t('banner.form.info.company')}
              placeholder={t('banner.form.info.companyPlaceholder')}
            />
          )}
        />

        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <div data-field-error={BANNER_ERROR_ANCHORS.Title}>
              <TextField
                label={`${t('banner.form.info.title')}*`}
                placeholder={t('banner.form.info.titlePlaceholder')}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
                size="md"
                fullWidth
                hasError={Boolean(errors.title)}
                {...(errors.title ? { helper: t('banner.form.validation.titleRequired') } : {})}
              />
            </div>
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-mini md:grid-cols-3">
        <Controller
          control={control}
          name="startDate"
          render={({ field }) => (
            <div data-field-error={BANNER_ERROR_ANCHORS.StartDate}>
              <DatePicker
                label={t('banner.form.info.startDate')}
                value={field.value}
                onChange={field.onChange}
                size="md"
                hasError={Boolean(errors.startDate)}
                {...(errors.startDate
                  ? { helper: t('banner.form.validation.startDateAfterEnd') }
                  : {})}
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name="endDate"
          render={({ field }) => (
            <div data-field-error={BANNER_ERROR_ANCHORS.EndDate}>
              <DatePicker
                label={t('banner.form.info.endDate')}
                value={field.value}
                onChange={field.onChange}
                size="md"
                hasError={Boolean(errors.endDate)}
                {...(errors.endDate
                  ? { helper: t('banner.form.validation.endDateBeforeStart') }
                  : {})}
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name="priority"
          render={({ field }) =>
            isPriorityLoading ? (
              <div className="flex flex-col gap-quark">
                <BannerFieldLabel label={t('banner.form.info.priority')} />
                <Skeleton className="h-[2.75rem] w-full rounded-sm" />
              </div>
            ) : isPriorityError ? (
              <div className="flex flex-col gap-quark">
                <BannerFieldLabel label={t('banner.form.info.priority')} />
                <p className="ts-caption-xxs text-base-danger-700">
                  {t('banner.form.toast.priorityLoadError')}
                </p>
                <LinkButton
                  type="button"
                  hierarchy="primary"
                  size="sm"
                  onClick={() => void refetchPriorities()}
                >
                  {t('banner.form.toast.loadErrorRetry')}
                </LinkButton>
              </div>
            ) : (
              <SelectInput
                label={t('banner.form.info.priority')}
                value={String(field.value)}
                onValueChange={value => field.onChange(Number(value))}
                size="md"
                fullWidth
                disabled={priorityOptions.length === 0}
              >
                {priorityOptions.map(option => (
                  <SelectInputItem
                    key={option.value}
                    value={String(option.value)}
                    custom={{ root: 'text-nowrap' }}
                  >
                    {option.label}
                  </SelectInputItem>
                ))}
              </SelectInput>
            )
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-mini md:grid-cols-2">
        <Controller
          control={control}
          name="url"
          render={({ field }) => (
            <div className="flex flex-col gap-quark" data-field-error={BANNER_ERROR_ANCHORS.Url}>
              <BannerFieldLabel
                label={t('banner.form.info.url')}
                info={t('banner.form.info.urlHint')}
              />
              <TextField
                placeholder={t('banner.form.info.urlPlaceholder')}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
                size="md"
                fullWidth
                hasError={Boolean(errors.url)}
                {...(errors.url ? { helper: t('banner.form.validation.urlInvalid') } : {})}
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name="duration"
          render={({ field }) => (
            <div
              className="flex flex-col gap-quark"
              data-field-error={BANNER_ERROR_ANCHORS.Duration}
            >
              <BannerFieldLabel
                label={t('banner.form.info.duration')}
                info={
                  isDurationLocked
                    ? t('banner.form.info.durationHintFromVideo', {
                        duration: formatDurationLabel(videoDurationSeconds ?? 0),
                      })
                    : t('banner.form.info.durationHintManual')
                }
              />
              <TextField
                value={String(isDurationLocked ? videoDurationSeconds : field.value)}
                onChange={e => field.onChange(Number(e.target.value.replace(/\D/g, '')) || 0)}
                size="md"
                fullWidth
                disabled={isDurationLocked}
                hasError={Boolean(errors.duration)}
                {...(errors.duration
                  ? { helper: t('banner.form.validation.durationRequired') }
                  : {})}
              />
            </div>
          )}
        />
      </div>
    </BannerFormSection>
  );
}
